/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { Modal } from '../../shared/modals/Modal';
import {
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { BulkPreviewItem as InventoryBulkPreviewRow } from '../domain/catalogTypes';
import type { CategoryOption } from '../domain/categoryOptions';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors } from '../../shared/theme/ticketColors';
import { parsePercentInput } from '../../shared/numeric/localeNumeric';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import { ModalStatePanel } from '../../shared/modals/ModalStatePanel';
import { ticketChrome as ticketUi } from '../../shared/ticket/ticketChrome.styles';
import { TicketModalHeader } from '../../shared/ticket/TicketModalHeader';
import { BulkCoefficientForm } from './BulkCoefficientForm';
import { BulkCoefficientPreview } from './BulkCoefficientPreview';
import { bulkCoefficientModalStyles as styles } from './bulkCoefficientModal.styles';
import { useBulkCoefficientModal, type BulkApplyTo } from './useBulkCoefficientModal';

const NARROW_MAX_WIDTH = 480;

/** Conteo honesto del alcance (con precio / sin precio) para el copy del paso 1. */
type BulkScopeCounts = { priced: number; unpriced: number };

type Props = {
  visible: boolean;
  bulkCategory: string | 'all';
  bulkSearch: string;
  bulkPercentInput: string;
  bulkApplyTo: BulkApplyTo;
  bulkPreview: InventoryBulkPreviewRow[];
  bulkScopeCounts?: BulkScopeCounts | null;
  categoryOptions: CategoryOption[];
  bulkPreviewLoading: boolean;
  bulkSaving: boolean;
  onClose: () => void;
  onCategoryChange: (value: string | 'all') => void;
  onSearchChange: (value: string) => void;
  onOpenScanner: () => void;
  showBarcodeCameraButton?: boolean;
  onSearchSubmitEditing?: () => void;
  autoFocusSearchOnWeb?: boolean;
  onPercentChange: (value: string) => void;
  onApplyToChange?: (value: BulkApplyTo) => void;
  onPreview: () => void;
  onConfirm: () => void;
  onClearPreview?: () => void;
  showApplyToSelector?: boolean;
  idleTitle?: string;
  idleSubtitle?: string;
  /** Kicker del header (contexto del módulo que lo abre: "Catálogo" o "Stock"). */
  kicker?: string;
};

export const BulkCoefficientModal = ({
  visible,
  bulkCategory,
  bulkSearch,
  bulkPercentInput,
  bulkApplyTo,
  bulkPreview,
  bulkScopeCounts = null,
  categoryOptions,
  bulkPreviewLoading,
  bulkSaving,
  onClose,
  onCategoryChange,
  onSearchChange,
  onOpenScanner,
  showBarcodeCameraButton = true,
  onSearchSubmitEditing,
  autoFocusSearchOnWeb = false,
  onPercentChange,
  onApplyToChange = () => {},
  onPreview,
  onConfirm,
  onClearPreview,
  showApplyToSelector = false,
  idleTitle = 'Actualizar en lote',
  idleSubtitle = 'Coeficiente · precios',
  kicker = 'Catálogo',
}: Props) => {
  const insets = useSafeAreaInsets();
  const modalBackdropPadding = useModalBackdropPadding();
  const { width, height } = useWindowDimensions();
  const narrow = width < NARROW_MAX_WIDTH;
  const layoutWide = width >= 640;

  const modalState = useBulkCoefficientModal({
    visible,
    bulkCategory,
    bulkSearch,
    bulkPercentInput,
    bulkApplyTo,
    bulkPreview,
    categoryOptions,
    onCategoryChange,
    onClearPreview,
  });

  const percentLabel = useMemo(() => {
    const raw = bulkPercentInput.trim();
    if (!raw) return 'lote';
    return `${raw.startsWith('-') ? '' : '+'}${raw}%`;
  }, [bulkPercentInput]);

  const title = modalState.hasPreview ? `Vista previa · ${percentLabel}` : idleTitle;
  const subtitle = modalState.hasPreview
    ? `${modalState.previewCount} producto${modalState.previewCount === 1 ? '' : 's'} · ${modalState.selectedCategoryLabel}`
    : idleSubtitle;
  const previewReady = parsePercentInput(bulkPercentInput) !== null;

  // La vista previa solo avanza con el botón «Ver vista previa»: nunca por
  // tipear un % válido ni por estado persistido del modal.
  const formBody = (
    <>
      <BulkCoefficientForm
        bulkCategory={bulkCategory}
        bulkSearch={bulkSearch}
        bulkPercentInput={bulkPercentInput}
        bulkApplyTo={bulkApplyTo}
        scopeCounts={bulkApplyTo === 'prices' ? bulkScopeCounts : null}
        categoryOptions={categoryOptions}
        showApplyToSelector={showApplyToSelector}
        showBarcodeCameraButton={showBarcodeCameraButton}
        onSearchChange={onSearchChange}
        onOpenScanner={onOpenScanner}
        onSearchSubmitEditing={onSearchSubmitEditing}
        autoFocusSearchOnWeb={autoFocusSearchOnWeb}
        onPercentChange={onPercentChange}
        onApplyToChange={onApplyToChange}
        categoryPickerOpen={modalState.categoryPickerOpen}
        setCategoryPickerOpen={modalState.setCategoryPickerOpen}
        categorySearch={modalState.categorySearch}
        setCategorySearch={modalState.setCategorySearch}
        selectedCategoryLabel={modalState.selectedCategoryLabel}
        selectedCategoryHint={modalState.selectedCategoryHint}
        visibleCategoryOptions={modalState.visibleCategoryOptions}
        selectCategory={modalState.selectCategory}
        scopeReady={modalState.scopeReady}
      />
      {bulkPreviewLoading ? <ModalStatePanel loading loadingMessage="Calculando vista previa…" /> : null}
    </>
  );

  const previewBody = (
    <BulkCoefficientPreview
      narrow={narrow}
      bulkApplyTo={bulkApplyTo}
      previewRows={modalState.previewRows}
      previewCount={modalState.previewCount}
      percentLabel={percentLabel}
      scopeLabel={modalState.selectedCategoryLabel}
      unpricedScopeCount={bulkApplyTo === 'prices' ? (bulkScopeCounts?.unpriced ?? 0) : 0}
    />
  );

  const foot = (
    <View style={ticketUi.foot}>
      {modalState.hasPreview ? (
        <>
          <TouchableOpacity
            style={[ticketUi.btnGhost, local.footBtn]}
            onPress={() => onClearPreview?.()}
            accessibilityRole="button"
            accessibilityLabel="Volver"
          >
            <Text style={ticketUi.btnGhostText}>Volver</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[ticketUi.btnGold, local.footBtn, bulkSaving && local.disabled]}
            onPress={onConfirm}
            disabled={bulkSaving}
            accessibilityRole="button"
            accessibilityLabel="Confirmar y aplicar"
          >
            <Text style={ticketUi.btnGoldText}>{bulkSaving ? 'Aplicando…' : 'Confirmar y aplicar'}</Text>
          </TouchableOpacity>
        </>
      ) : (
        <>
          <TouchableOpacity
            style={[ticketUi.btnGhost, local.footBtn]}
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Cancelar"
          >
            <Text style={ticketUi.btnGhostText}>Cancelar</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              ticketUi.btnGold,
              local.footBtn,
              (bulkPreviewLoading || !previewReady) && local.disabled,
            ]}
            onPress={onPreview}
            disabled={bulkPreviewLoading || !previewReady}
            accessibilityRole="button"
            accessibilityLabel="Ver vista previa"
          >
            <Text style={ticketUi.btnGoldText}>
              {bulkPreviewLoading ? 'Cargando…' : 'Ver vista previa'}
            </Text>
          </TouchableOpacity>
        </>
      )}
    </View>
  );

  const header = (
    <TicketModalHeader
      kicker={kicker}
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      compact={!layoutWide}
    />
  );

  const main = modalState.hasPreview ? (
    <View style={local.previewPane}>{previewBody}</View>
  ) : (
    <ModalAwareScrollView
      style={local.scroll}
      contentContainerStyle={[local.scrollContent, narrow && styles.bulkModalContentNarrow]}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}
      bottomOffset={spacing.sm}
    >
      {formBody}
    </ModalAwareScrollView>
  );

  if (layoutWide) {
    return (
      <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
        <View style={[local.wideRoot, modalBackdropPadding]}>
          <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
          <View
            style={[
              ticketUi.dialogCard,
              local.dialogCard,
              { maxWidth: Math.min(860, width - 40), maxHeight: height * 0.92, width: '100%' },
              // La tabla de vista previa necesita la card alta; el formulario no.
              modalState.hasPreview ? { height: height * 0.88 } : null,
            ]}
          >
            {header}
            {main}
            {foot}
          </View>
        </View>
      </Modal>
    );
  }

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={ticketUi.sheetBackdrop}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
        <View
          style={[
            ticketUi.sheetCard,
            local.sheetFlex,
            { paddingBottom: Math.max(insets.bottom, spacing.sm) },
          ]}
        >
          <View style={ticketUi.grabber} />
          {header}
          {main}
          {foot}
        </View>
      </View>
    </Modal>
  );
};

const local = StyleSheet.create({
  wideRoot: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dialogCard: {
    overflow: 'hidden',
    zIndex: 1,
  },
  sheetFlex: {
    flex: 1,
  },
  scroll: {
    flexShrink: 1,
    flexGrow: 1,
  },
  scrollContent: {
    paddingHorizontal: 18,
    paddingBottom: spacing.md,
    gap: spacing.sm,
  },
  previewPane: {
    flex: 1,
    minHeight: 0,
    paddingHorizontal: 18,
  },
  footBtn: {
    flex: 1,
  },
  disabled: {
    opacity: 0.45,
  },
});
